import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  Users, TrendingUp, DollarSign, Mail, ArrowUpRight, ArrowDownRight,
  Clock, Target, BarChart3, ChevronRight, Briefcase, FileDown, Loader2, Send, CalendarClock, CheckCircle
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  nuevo: { label: "Nuevo", color: "#3b82f6" },
  contactado: { label: "Contactado", color: "#f59e0b" },
  negociacion: { label: "Negociación", color: "#8b5cf6" },
  convertido: { label: "Convertido", color: "#10b981" },
  perdido: { label: "Perdido", color: "#ef4444" },
};

const INTEREST_CONFIG = {
  general: { label: "Demo general", color: "#94a3b8" },
  erp: { label: "Sistema ERP", color: "#3b82f6" },
  crm: { label: "CRM", color: "#06b6d4" },
  inventory: { label: "Inventarios", color: "#8b5cf6" },
  billing: { label: "Facturación", color: "#10b981" },
  custom: { label: "A medida", color: "#f59e0b" },
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0b1221] border border-white/10 p-3 rounded-sm text-sm">
      <p className="text-white font-medium mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.color }}>{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

const KpiCard = ({ title, value, subtitle, icon: Icon, color, trend, trendValue, to, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className="glass-card p-5 relative overflow-hidden group"
    data-testid={`kpi-${title.toLowerCase().replace(/\s/g, "-")}`}
  >
    <motion.div
      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
      style={{ background: `radial-gradient(circle at top right, ${color}08, transparent)` }}
    />
    <div className="flex items-start justify-between mb-3 relative z-10">
      <div className="w-10 h-10 flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      {trend && (
        <div className={`flex items-center gap-0.5 text-xs font-medium ${trend === "up" ? "text-emerald-400" : "text-red-400"}`}>
          {trend === "up" ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
          {trendValue}
        </div>
      )}
    </div>
    <p className="text-2xl font-bold text-white relative z-10">{value}</p>
    <div className="flex items-center justify-between mt-1 relative z-10">
      <p className="text-[#94a3b8] text-xs">{title}</p>
      {to && (
        <Link to={to} className="text-[#3b82f6] text-xs flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
          Ver <ChevronRight className="w-3 h-3" />
        </Link>
      )}
    </div>
    {subtitle && <p className="text-[#94a3b8] text-[10px] mt-0.5 relative z-10">{subtitle}</p>}
  </motion.div>
);

const FunnelStep = ({ label, count, total, color, isLast }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex-1 relative">
      <div className="text-center mb-2">
        <p className="text-white font-bold text-lg">{count}</p>
        <p className="text-[#94a3b8] text-[10px]">{label}</p>
      </div>
      <div className="h-10 relative mx-1">
        <motion.div
          className="absolute bottom-0 left-0 right-0 rounded-t-sm"
          style={{ backgroundColor: color }}
          initial={{ height: 0 }}
          animate={{ height: `${Math.max(pct, 8)}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        />
      </div>
      <p className="text-center text-[10px] mt-1 font-medium" style={{ color }}>{pct}%</p>
      {!isLast && (
        <div className="absolute top-3 -right-2 text-[#94a3b8] text-xs">→</div>
      )}
    </div>
  );
};

const AnalyticsDashboard = ({ auth, content }) => {
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [reportHistory, setReportHistory] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [leadsRes, contactsRes] = await Promise.all([
          axios.get(`${API}/admin/leads`, { auth: { username: auth.username, password: auth.password } }),
          axios.get(`${API}/admin/contacts`, { auth: { username: auth.username, password: auth.password } }).catch(() => ({ data: [] })),
        ]);
        setLeads(leadsRes.data?.items || leadsRes.data || []);
        setContacts(contactsRes.data?.items || contactsRes.data || []);
        // Fetch report history
        try {
          const histRes = await axios.get(`${API}/admin/reports/history`, {
            auth: { username: auth.username, password: auth.password },
          });
          setReportHistory(histRes.data || []);
        } catch {}
      } catch (e) {
        // Error handled silently — analytics is non-critical
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [auth]);

  const sendReportNow = async () => {
    setSendingEmail(true);
    setEmailSent(false);
    try {
      await axios.post(`${API}/admin/reports/send-now`, {}, {
        auth: { username: auth.username, password: auth.password },
      });
      setEmailSent(true);
      setTimeout(() => setEmailSent(false), 5000);
    } catch {
      toast.error("Error al enviar reporte");
    } finally {
      setSendingEmail(false);
    }
  };

  const downloadReport = async () => {
    setGeneratingPdf(true);
    try {
      const response = await axios.get(`${API}/admin/reports/pipeline`, {
        auth: { username: auth.username, password: auth.password },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `reporte_pipeline_${new Date().toISOString().split("T")[0]}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Error al generar PDF");
    } finally {
      setGeneratingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-[#3b82f6] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ── Compute Metrics ──
  const now = new Date();
  const weekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
  const twoWeeksAgo = new Date(now - 14 * 24 * 60 * 60 * 1000);

  const leadsThisWeek = leads.filter((l) => new Date(l.created_at) >= weekAgo).length;
  const leadsLastWeek = leads.filter((l) => {
    const d = new Date(l.created_at);
    return d >= twoWeeksAgo && d < weekAgo;
  }).length;
  const weekTrend = leadsLastWeek > 0 ? Math.round(((leadsThisWeek - leadsLastWeek) / leadsLastWeek) * 100) : leadsThisWeek > 0 ? 100 : 0;

  const statusCounts = leads.reduce((acc, l) => {
    const s = l.status || "nuevo";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const convertedCount = statusCounts.convertido || 0;
  const lostCount = statusCounts.perdido || 0;
  const closedCount = convertedCount + lostCount;
  const conversionRate = closedCount > 0 ? Math.round((convertedCount / closedCount) * 100) : 0;
  const overallConversion = leads.length > 0 ? Math.round((convertedCount / leads.length) * 100) : 0;

  // Leads by week (last 8 weeks)
  const weeklyData = [];
  for (let i = 7; i >= 0; i--) {
    const start = new Date(now - (i + 1) * 7 * 24 * 60 * 60 * 1000);
    const end = new Date(now - i * 7 * 24 * 60 * 60 * 1000);
    const count = leads.filter((l) => {
      const d = new Date(l.created_at);
      return d >= start && d < end;
    }).length;
    weeklyData.push({
      semana: `S${8 - i}`,
      leads: count,
    });
  }

  // Interest distribution
  const interestData = Object.entries(
    leads.reduce((acc, l) => {
      const interest = l.interest || "general";
      acc[interest] = (acc[interest] || 0) + 1;
      return acc;
    }, {})
  ).map(([key, value]) => ({
    name: INTEREST_CONFIG[key]?.label || key,
    value,
    color: INTEREST_CONFIG[key]?.color || "#94a3b8",
  }));

  // Status distribution for area chart
  const statusData = Object.entries(statusCounts).map(([key, value]) => ({
    name: STATUS_CONFIG[key]?.label || key,
    value,
    color: STATUS_CONFIG[key]?.color || "#94a3b8",
  }));

  // Funnel data
  const funnelStages = [
    { id: "nuevo", label: "Nuevos", count: statusCounts.nuevo || 0, color: "#3b82f6" },
    { id: "contactado", label: "Contactados", count: statusCounts.contactado || 0, color: "#f59e0b" },
    { id: "negociacion", label: "Negociación", count: statusCounts.negociacion || 0, color: "#8b5cf6" },
    { id: "convertido", label: "Convertidos", count: statusCounts.convertido || 0, color: "#10b981" },
  ];

  // Recent leads (last 5)
  const recentLeads = [...leads].slice(0, 5);

  // Average response (mock based on status)
  const contactedOrBetter = leads.filter((l) => l.status && l.status !== "nuevo").length;
  const responseRate = leads.length > 0 ? Math.round((contactedOrBetter / leads.length) * 100) : 0;

  // Source distribution
  const SOURCE_CONFIG = {
    demo_tour: { label: "Tour Demo", color: "#3b82f6" },
    version_quiz: { label: "Quiz Version", color: "#06b6d4" },
    contact_form: { label: "Formulario", color: "#10b981" },
    demo_request: { label: "Solicitud Demo", color: "#8b5cf6" },
  };
  const sourceData = Object.entries(
    leads.reduce((acc, l) => {
      const src = l.source || "demo_tour";
      acc[src] = (acc[src] || 0) + 1;
      return acc;
    }, {})
  ).map(([key, value]) => ({
    name: SOURCE_CONFIG[key]?.label || key,
    value,
    color: SOURCE_CONFIG[key]?.color || "#94a3b8",
  })).sort((a, b) => b.value - a.value);

  // Daily leads (last 14 days)
  const dailyData = [];
  for (let i = 13; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const dayStr = d.toISOString().split("T")[0];
    const count = leads.filter((l) => l.created_at?.startsWith?.(dayStr) || new Date(l.created_at).toISOString().split("T")[0] === dayStr).length;
    dailyData.push({ dia: `${d.getDate()}/${d.getMonth() + 1}`, leads: count });
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="analytics-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard Analytics</h1>
          <p className="text-[#94a3b8] text-sm">Métricas de rendimiento de tu funnel de ventas</p>
        </div>
        <div className="flex items-center gap-3">
          <motion.button
            onClick={sendReportNow}
            disabled={sendingEmail || emailSent}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border transition-colors ${
              emailSent
                ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                : "bg-white/5 text-[#94a3b8] border-white/10 hover:text-white hover:border-[#3b82f6]/30"
            }`}
            whileHover={!emailSent ? { scale: 1.02 } : {}}
            whileTap={{ scale: 0.97 }}
            data-testid="send-report-email-btn"
          >
            {sendingEmail ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</>
            ) : emailSent ? (
              <><CheckCircle className="w-4 h-4" /> Enviado</>
            ) : (
              <><Send className="w-4 h-4" /> Enviar por Email</>
            )}
          </motion.button>
          <motion.button
            onClick={downloadReport}
            disabled={generatingPdf}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#3b82f6] text-white text-sm font-medium hover:bg-[#3b82f6]/90 transition-colors disabled:opacity-50"
            whileHover={{ boxShadow: "0 0 25px rgba(59,130,246,0.3)" }}
            whileTap={{ scale: 0.97 }}
            data-testid="download-report-btn"
          >
            {generatingPdf ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Generando...</>
            ) : (
              <><FileDown className="w-4 h-4" /> Descargar PDF</>
            )}
          </motion.button>
        </div>
      </div>

      {/* Schedule info bar */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-4 mb-6 flex items-center justify-between"
        data-testid="schedule-info"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[#3b82f6]/10 flex items-center justify-center">
            <CalendarClock className="w-4 h-4 text-[#3b82f6]" />
          </div>
          <div>
            <p className="text-white text-sm font-medium">Reporte Automático Semanal</p>
            <p className="text-[#94a3b8] text-xs">Cada lunes a las 8:00 AM UTC → lametaforashipping@gmail.com</p>
          </div>
        </div>
        {reportHistory.length > 0 && (
          <span className="text-[#94a3b8] text-xs">
            Último envío: {new Date(reportHistory[0].sent_at).toLocaleDateString("es", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
          </span>
        )}
      </motion.div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard
          title="Total Leads" value={leads.length}
          icon={Users} color="#3b82f6"
          trend={weekTrend >= 0 ? "up" : "down"}
          trendValue={`${weekTrend >= 0 ? "+" : ""}${weekTrend}% semanal`}
          to="/admin/leads" delay={0.05}
        />
        <KpiCard
          title="Tasa de Conversión" value={`${overallConversion}%`}
          subtitle={`${convertedCount} convertidos de ${leads.length}`}
          icon={Target} color="#10b981" delay={0.1}
          to="/admin/leads"
        />
        <KpiCard
          title="Leads esta Semana" value={leadsThisWeek}
          subtitle={`vs ${leadsLastWeek} semana anterior`}
          icon={TrendingUp} color="#06b6d4" delay={0.15}
          trend={leadsThisWeek >= leadsLastWeek ? "up" : "down"}
          trendValue={leadsThisWeek >= leadsLastWeek ? "En alza" : "En baja"}
        />
        <KpiCard
          title="Tasa de Respuesta" value={`${responseRate}%`}
          subtitle={`${contactedOrBetter} leads con seguimiento`}
          icon={Clock} color="#8b5cf6" delay={0.2}
        />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        {/* Conversion Funnel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="lg:col-span-2 glass-card p-5"
          data-testid="conversion-funnel"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-white font-semibold">Funnel de Conversión</h3>
              <p className="text-[#94a3b8] text-xs">Progreso de leads por etapa</p>
            </div>
            <div className="flex items-center gap-2 text-xs text-[#94a3b8]">
              <span>Win rate: </span>
              <span className="text-[#10b981] font-semibold">{conversionRate}%</span>
            </div>
          </div>
          <div className="flex items-end gap-1 h-32">
            {funnelStages.map((stage, i) => (
              <FunnelStep
                key={stage.id}
                label={stage.label}
                count={stage.count}
                total={leads.length}
                color={stage.color}
                isLast={i === funnelStages.length - 1}
              />
            ))}
          </div>
          {leads.length === 0 && (
            <p className="text-[#94a3b8] text-sm text-center mt-4">Los datos del funnel aparecerán cuando captures tus primeros leads</p>
          )}
        </motion.div>

        {/* Interest Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-card p-5"
          data-testid="interest-distribution"
        >
          <h3 className="text-white font-semibold mb-1">Intereses</h3>
          <p className="text-[#94a3b8] text-xs mb-3">Distribución por tipo de interés</p>
          {interestData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={130}>
                <PieChart>
                  <Pie data={interestData} cx="50%" cy="50%" innerRadius={35} outerRadius={55} paddingAngle={3} dataKey="value">
                    {interestData.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-1.5 mt-2">
                {interestData.map((d) => (
                  <div key={d.name} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: d.color }} />
                      <span className="text-[#94a3b8]">{d.name}</span>
                    </span>
                    <span className="text-white font-medium">{d.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-32 text-[#94a3b8] text-sm">Sin datos aún</div>
          )}
        </motion.div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        {/* Weekly Leads Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="lg:col-span-2 glass-card p-5"
          data-testid="weekly-leads-chart"
        >
          <h3 className="text-white font-semibold mb-1">Leads por Semana</h3>
          <p className="text-[#94a3b8] text-xs mb-4">Últimas 8 semanas</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={weeklyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="semana" stroke="#94a3b8" fontSize={11} tickLine={false} />
              <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="leads" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Leads" />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        {/* Status Distribution */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="glass-card p-5"
          data-testid="status-distribution"
        >
          <h3 className="text-white font-semibold mb-1">Estado del Pipeline</h3>
          <p className="text-[#94a3b8] text-xs mb-4">Leads por estado actual</p>
          <div className="space-y-3">
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
              const count = statusCounts[key] || 0;
              const pct = leads.length > 0 ? (count / leads.length) * 100 : 0;
              return (
                <div key={key}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: cfg.color }} />
                      <span className="text-[#94a3b8]">{cfg.label}</span>
                    </span>
                    <span className="text-white font-medium">{count}</span>
                  </div>
                  <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ backgroundColor: cfg.color }}
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      </div>

      {/* Daily Leads + Source Distribution */}
      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.42 }}
          className="lg:col-span-2 glass-card p-5"
          data-testid="daily-leads-chart"
        >
          <h3 className="text-white font-semibold mb-1">Leads por Dia</h3>
          <p className="text-[#94a3b8] text-xs mb-4">Ultimos 14 dias</p>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={dailyData}>
              <defs>
                <linearGradient id="colorDaily" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#06b6d4" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="dia" stroke="#94a3b8" fontSize={10} tickLine={false} />
              <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="leads" stroke="#06b6d4" strokeWidth={2} fill="url(#colorDaily)" name="Leads" />
            </AreaChart>
          </ResponsiveContainer>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.44 }}
          className="glass-card p-5"
          data-testid="source-distribution"
        >
          <h3 className="text-white font-semibold mb-1">Fuentes de Leads</h3>
          <p className="text-[#94a3b8] text-xs mb-4">De donde vienen tus prospectos</p>
          <div className="space-y-3">
            {sourceData.length > 0 ? sourceData.map((src) => {
              const pct = leads.length > 0 ? (src.value / leads.length) * 100 : 0;
              return (
                <div key={src.name}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: src.color }} />
                      <span className="text-[#94a3b8]">{src.name}</span>
                    </span>
                    <span className="text-white font-medium">{src.value} ({Math.round(pct)}%)</span>
                  </div>
                  <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ backgroundColor: src.color }}
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                    />
                  </div>
                </div>
              );
            }) : (
              <div className="text-center py-4 text-[#94a3b8] text-sm">Sin datos aun</div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Recent Leads + Quick Stats */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent Leads */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="lg:col-span-2 glass-card p-5"
          data-testid="recent-leads"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold">Leads Recientes</h3>
            <Link to="/admin/leads" className="text-[#3b82f6] text-xs flex items-center gap-1 hover:underline">
              Ver todos <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          {recentLeads.length > 0 ? (
            <div className="space-y-3">
              {recentLeads.map((lead, i) => {
                const status = STATUS_CONFIG[lead.status || "nuevo"];
                return (
                  <motion.div
                    key={lead.id}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + i * 0.05 }}
                    className="flex items-center gap-3 p-3 rounded-sm hover:bg-white/5 transition-colors"
                  >
                    <div
                      className="w-9 h-9 flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                      style={{ background: `linear-gradient(135deg, ${status.color}, ${status.color}80)` }}
                    >
                      {lead.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{lead.name}</p>
                      <p className="text-[#94a3b8] text-xs truncate">{lead.email}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span
                        className="text-[10px] px-2 py-0.5 font-medium"
                        style={{ backgroundColor: `${status.color}20`, color: status.color }}
                      >
                        {status.label}
                      </span>
                      <p className="text-[#94a3b8] text-[10px] mt-1">
                        {lead.created_at ? new Date(lead.created_at).toLocaleDateString("es", { day: "numeric", month: "short" }) : ""}
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="w-10 h-10 text-[#3b82f6]/20 mx-auto mb-3" />
              <p className="text-[#94a3b8] text-sm">Los leads aparecerán aquí cuando los visitantes completen el tour del demo</p>
            </div>
          )}
        </motion.div>

        {/* Content Quick Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="glass-card p-5"
          data-testid="content-stats"
        >
          <h3 className="text-white font-semibold mb-4">Contenido del Sitio</h3>
          <div className="space-y-3">
            {[
              { label: "Servicios", count: content?.services?.services?.length || 0, color: "#3b82f6", to: "/admin/services" },
              { label: "Planes", count: content?.pricing?.plans?.length || 0, color: "#10b981", to: "/admin/pricing" },
              { label: "Testimonios", count: content?.testimonials?.testimonials?.length || 0, color: "#f59e0b", to: "/admin/testimonials" },
              { label: "FAQs", count: content?.faq?.faqs?.length || 0, color: "#8b5cf6", to: "/admin/faq" },
              { label: "Mensajes", count: contacts.length, color: "#06b6d4", to: "/admin/contacts" },
            ].map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className="flex items-center justify-between p-3 hover:bg-white/5 transition-colors rounded-sm group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-[#94a3b8] text-sm group-hover:text-white transition-colors">{item.label}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-white font-semibold text-sm">{item.count}</span>
                  <ChevronRight className="w-3 h-3 text-[#94a3b8] opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </Link>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default AnalyticsDashboard;
